/**
 * Bundled FR/EN strings for the QA Meter widget.
 *
 * Keys are the donor's `qaMeter.*` keys with the `qaMeter.` prefix stripped.
 * Values are copied verbatim from the donor locale files.
 *
 * Two new keys not present in the donor are appended at the end of each record:
 *   - `panel.noTests`
 *   - `panel.unknownPage`
 */

export const QA_STRINGS_FR = {
  'pastille.label': 'Suivi des tests',
  'pastille.loading': 'Chargement…',
  'pastille.unavailable': 'Statuts indisponibles',
  'pastille.unknownPage': 'Page inconnue',
  'pastille.unknownPageHint': "Cette route n’est pas encore au plan du site des tests.",
  'panel.freshness': 'Statuts g\xe9n\xe9r\xe9s le {date} \xb7 version {version} \xb7 env {env}',
  'panel.noScenarios': 'Aucun sc\xe9nario li\xe9 \xe0 cette page.',
  'panel.openSitemap': 'Voir le plan du site',
  'panel.sitemapLink': 'Plan du site',
  'panel.scenarioCountOne': '{count} sc\xe9nario',
  'panel.scenarioCountMany': '{count} sc\xe9narios',
  'panel.laneAutomated': 'Automatis\xe9',
  'panel.laneDev': 'D\xe9v',
  'panel.laneClient': 'Client',
  'lane.notTested': 'Non test\xe9',
  'lane.validatedBy': 'Valid\xe9 par {by}',
  'lane.rejectedBy': 'Rejet\xe9 par {by}',
  'lane.stale': 'Valid\xe9 en v{version}, version actuelle v{current}',
  'lane.clientPending': 'Non test\xe9 — plateforme requise',
  'lane.bot': 'Automatis\xe9 (bot)',
  'lane.code': 'D\xe9veloppeur (code)',
  'lane.human': 'Humain',
  'laneState.ok': '\xc0 jour',
  'laneState.issue': 'Probl\xe8me',
  'laneState.none': 'Non fait / p\xe9rim\xe9',
  'color.green': 'Valid\xe9',
  'color.yellow': 'En attente',
  'color.red': 'En \xe9chec',
  'color.gray': 'Non couvert',
  'automated.passing': 'R\xe9ussi',
  'automated.failing': 'En \xe9chec',
  'automated.flaky': 'Instable',
  'automated.neverRun': 'Jamais ex\xe9cut\xe9',
  'source.testgen': 'g\xe9n\xe9r\xe9',
  'source.testgenHint': 'Sc\xe9nario g\xe9n\xe9r\xe9 automatiquement (testgen)',
  'suite.cases': '{count} cas',
  'case.covered': 'auto',
  'case.manual': 'manuel',
  'case.blocked': 'bloqu\xe9',
  'case.openQuestion': 'question produit',
  'grounding.verified': 'attendu v\xe9rifi\xe9',
  'grounding.confirmLive': '\xe0 confirmer en live',
  'grounding.productQuestion': 'd\xe9cision produit',
  'modal.title': 'Plan du site — suivi des tests',
  'modal.close': 'Fermer',
  'modal.tabPages': 'Pages',
  'modal.tabScenarios': 'Sc\xe9narios',
  'modal.youAreHere': 'vous \xeates ici',
  'modal.search': 'Filtrer les sc\xe9narios…',
  'modal.noMatch': 'Aucun sc\xe9nario ne correspond au filtre.',
  'modal.traversed': 'Travers\xe9es',
  'modal.appliesTo': '{count} pages',
  'modal.kpiPagesCovered': 'Pages avec ≥ 1 sc\xe9nario',
  'modal.kpiScenariosPassing': 'Sc\xe9narios passants',
  // New keys (not in donor)
  'panel.noTests': 'Aucun test ne couvre encore cette page.',
  'panel.unknownPage': 'Page inconnue du registre QA.',
} as const

export const QA_STRINGS_EN = {
  'pastille.label': 'Test tracking',
  'pastille.loading': 'Loading…',
  'pastille.unavailable': 'Statuses unavailable',
  'pastille.unknownPage': 'Unknown page',
  'pastille.unknownPageHint': "This route isn’t in the test sitemap yet.",
  'panel.freshness': 'Statuses generated on {date} \xb7 version {version} \xb7 env {env}',
  'panel.noScenarios': 'No scenario linked to this page.',
  'panel.openSitemap': 'View sitemap',
  'panel.sitemapLink': 'Sitemap',
  'panel.scenarioCountOne': '{count} scenario',
  'panel.scenarioCountMany': '{count} scenarios',
  'panel.laneAutomated': 'Automated',
  'panel.laneDev': 'Dev',
  'panel.laneClient': 'Client',
  'lane.notTested': 'Not tested',
  'lane.validatedBy': 'Validated by {by}',
  'lane.rejectedBy': 'Rejected by {by}',
  'lane.stale': 'Validated in v{version}, current version v{current}',
  'lane.clientPending': 'Not tested — platform required',
  'lane.bot': 'Automated (bot)',
  'lane.code': 'Developer (code)',
  'lane.human': 'Human',
  'laneState.ok': 'Up to date',
  'laneState.issue': 'Issue',
  'laneState.none': 'Not done / stale',
  'color.green': 'Validated',
  'color.yellow': 'Pending',
  'color.red': 'Failing',
  'color.gray': 'Not covered',
  'automated.passing': 'Passing',
  'automated.failing': 'Failing',
  'automated.flaky': 'Flaky',
  'automated.neverRun': 'Never run',
  'source.testgen': 'generated',
  'source.testgenHint': 'Auto-generated scenario (testgen)',
  'suite.cases': '{count} cases',
  'case.covered': 'auto',
  'case.manual': 'manual',
  'case.blocked': 'blocked',
  'case.openQuestion': 'product question',
  'grounding.verified': 'expected verified',
  'grounding.confirmLive': 'confirm live',
  'grounding.productQuestion': 'product decision',
  'modal.title': 'Sitemap — test tracking',
  'modal.close': 'Close',
  'modal.tabPages': 'Pages',
  'modal.tabScenarios': 'Scenarios',
  'modal.youAreHere': 'you are here',
  'modal.search': 'Filter scenarios…',
  'modal.noMatch': 'No scenario matches the filter.',
  'modal.traversed': 'Traversed',
  'modal.appliesTo': '{count} pages',
  'modal.kpiPagesCovered': 'Pages with ≥ 1 scenario',
  'modal.kpiScenariosPassing': 'Passing scenarios',
  // New keys (not in donor)
  'panel.noTests': 'No tests cover this page yet.',
  'panel.unknownPage': 'Page unknown to the QA registry.',
} as const

export type QaStringKey = keyof typeof QA_STRINGS_FR
export type QaStrings = Record<QaStringKey, string>

export function resolveStrings(locale: 'fr' | 'en'): QaStrings {
  return locale === 'en' ? QA_STRINGS_EN : QA_STRINGS_FR
}
